import React, { useState, useMemo, useCallback } from 'react';
import { useGoalsOverview } from '../../swr-config';
import { GoalsStreaksPanel } from './GoalsStreaksPanel';
import type { GoalsFixtureSummary, GoalsOverviewResponse } from '../../types';
import useSWR from 'swr';

type SortKey = 'kickoff' | 'btts' | 'o25' | 'btts_o25' | 'home_btts' | 'away_btts' | 'home_o25' | 'away_o25' | 'xg';
type SortDir = 'asc' | 'desc';
type DateRange = 1 | 2 | 5 | 7;

interface GoalsOverviewProps {
  selectedDate: string | null;
  onSelectFixture: (fixtureId: number) => void;
  onDateChange: (date: string) => void;
  onGoToSaved: () => void;
}

export function GoalsOverview({
  selectedDate,
  onSelectFixture,
  onDateChange,
  onGoToSaved,
}: GoalsOverviewProps) {
  const [sortKey, setSortKey] = useState<SortKey>('btts');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [minBttsFilter, setMinBttsFilter] = useState<number | null>(null);
  const [minO25Filter, setMinO25Filter] = useState<number | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>(1);

  const effectiveDate = selectedDate || new Date().toISOString().split('T')[0];

  // Generate array of dates for the range
  const dateList = useMemo(() => {
    const dates: string[] = [];
    const start = new Date(effectiveDate);
    for (let i = 0; i < dateRange; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      dates.push(d.toISOString().split('T')[0]);
    }
    return dates;
  }, [effectiveDate, dateRange]);

  // Fetch data for all dates in range
  const { data: day1 } = useGoalsOverview({ date: dateList[0], sort: 'kickoff', minBtts: minBttsFilter, minO25: minO25Filter });
  const { data: day2 } = useGoalsOverview(dateRange >= 2 ? { date: dateList[1], sort: 'kickoff', minBtts: minBttsFilter, minO25: minO25Filter } : undefined);
  const { data: day3 } = useGoalsOverview(dateRange >= 5 ? { date: dateList[2], sort: 'kickoff', minBtts: minBttsFilter, minO25: minO25Filter } : undefined);
  const { data: day4 } = useGoalsOverview(dateRange >= 5 ? { date: dateList[3], sort: 'kickoff', minBtts: minBttsFilter, minO25: minO25Filter } : undefined);
  const { data: day5 } = useGoalsOverview(dateRange >= 5 ? { date: dateList[4], sort: 'kickoff', minBtts: minBttsFilter, minO25: minO25Filter } : undefined);
  const { data: day6 } = useGoalsOverview(dateRange >= 7 ? { date: dateList[5], sort: 'kickoff', minBtts: minBttsFilter, minO25: minO25Filter } : undefined);
  const { data: day7 } = useGoalsOverview(dateRange >= 7 ? { date: dateList[6], sort: 'kickoff', minBtts: minBttsFilter, minO25: minO25Filter } : undefined);

  // Combine all fixtures
  const data = useMemo(() => {
    const allDays = [day1, day2, day3, day4, day5, day6, day7].filter(Boolean) as GoalsOverviewResponse[];
    if (!allDays.length) return null;

    const allFixtures: GoalsFixtureSummary[] = [];
    const allStreaks = day1?.streaks || [];
    let totalFixtures = 0;
    let fixturesWithPredictions = 0;

    allDays.forEach(d => {
      if (d?.fixtures) {
        allFixtures.push(...d.fixtures);
        totalFixtures += d.total_fixtures || 0;
        fixturesWithPredictions += d.fixtures_with_predictions || 0;
      }
    });

    return {
      fixtures: allFixtures,
      streaks: allStreaks,
      total_fixtures: totalFixtures,
      fixtures_with_predictions: fixturesWithPredictions,
    };
  }, [day1, day2, day3, day4, day5, day6, day7]);

  const isLoading = !day1;
  const error = null;

  const goToDate = useCallback((daysOffset: number) => {
    const current = new Date(effectiveDate);
    current.setDate(current.getDate() + daysOffset);
    onDateChange(current.toISOString().split('T')[0]);
  }, [effectiveDate, onDateChange]);

  const formatDisplayDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const targetDate = new Date(dateStr);
    targetDate.setHours(0, 0, 0, 0);

    const isToday = targetDate.getTime() === today.getTime();
    const formatted = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

    if (isToday) {
      return `Today`;
    }
    return formatted;
  };

  const formatKickoff = (kickoff: string | null) => {
    if (!kickoff) return '-';
    const date = new Date(kickoff);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const formatDateShort = (kickoff: string | null) => {
    if (!kickoff) return '-';
    const date = new Date(kickoff);
    return date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' });
  };

  const formatProb = (prob: number | null | undefined) => {
    if (prob == null) return '-';
    return `${(prob * 100).toFixed(0)}%`;
  };

  const getProbClass = (prob: number | null | undefined) => {
    if (prob == null) return '';
    if (prob >= 0.65) return 'goals-prob-high';
    if (prob >= 0.5) return 'goals-prob-mid';
    return 'goals-prob-low';
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    } else {
      setSortKey(key);
      setSortDir(key === 'kickoff' ? 'asc' : 'desc');
    }
  };

  const getSortValue = (f: GoalsFixtureSummary, key: SortKey): number => {
    switch (key) {
      case 'kickoff': return f.kickoff ? new Date(f.kickoff).getTime() : 0;
      case 'btts': return f.btts_yes_prob ?? 0;
      case 'o25': return f.over_2_5_prob ?? 0;
      case 'btts_o25': return f.btts_o2_5_prob ?? 0;
      case 'home_btts': return f.home_win_btts_prob ?? 0;
      case 'away_btts': return f.away_win_btts_prob ?? 0;
      case 'home_o25': return f.home_win_o2_5_prob ?? 0;
      case 'away_o25': return f.away_win_o2_5_prob ?? 0;
      case 'xg': return f.expected_total_goals ?? 0;
      default: return 0;
    }
  };

  const sortedFixtures = useMemo(() => {
    if (!data?.fixtures) return [];
    const sorted = [...data.fixtures].sort((a, b) => {
      const aVal = getSortValue(a, sortKey);
      const bVal = getSortValue(b, sortKey);
      return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
    });
    return sorted;
  }, [data?.fixtures, sortKey, sortDir]);

  const tooltips: Record<SortKey, string> = {
    kickoff: 'Kickoff Time',
    btts: 'Both Teams To Score probability',
    o25: 'Over 2.5 Goals probability',
    btts_o25: 'BTTS + Over 2.5 Goals combo',
    home_btts: 'Home Win + BTTS combo',
    away_btts: 'Away Win + BTTS combo',
    home_o25: 'Home Win + Over 2.5 Goals combo',
    away_o25: 'Away Win + Over 2.5 Goals combo',
    xg: 'Expected Total Goals',
  };

  const SortHeader = ({ label, sortKeyName }: { label: string; sortKeyName: SortKey }) => (
    <th
      className={`goals-th-sortable ${sortKey === sortKeyName ? 'goals-th-active' : ''}`}
      onClick={() => handleSort(sortKeyName)}
      title={tooltips[sortKeyName]}
    >
      <span>{label}</span>
      {sortKey === sortKeyName && (
        <span className="goals-sort-arrow">{sortDir === 'desc' ? '↓' : '↑'}</span>
      )}
    </th>
  );

  if (isLoading) {
    return (
      <div className="goals-overview">
        <div className="goals-loading">
          <div className="loading-spinner" />
          <p>Loading goals predictions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="goals-overview">
        <div className="goals-error">
          <p>Failed to load goals data</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="goals-overview">
      {/* Header */}
      <div className="goals-overview-header">
        <div className="goals-header-row">
          <h1>Goals Hub</h1>
          <div className="goals-header-actions">
            <button className="goals-saved-btn" onClick={onGoToSaved}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Date Controls */}
        <div className="goals-date-controls">
          <div className="goals-date-picker">
            <button className="goals-date-nav" onClick={() => goToDate(-1)}>‹</button>
            <span className="goals-date-display">{formatDisplayDate(effectiveDate)}</span>
            <button className="goals-date-nav" onClick={() => goToDate(1)}>›</button>
          </div>
          <div className="goals-range-chips">
            {([1, 2, 5, 7] as DateRange[]).map((days) => (
              <button
                key={days}
                className={`goals-chip goals-chip-sm ${dateRange === days ? 'goals-chip-active' : ''}`}
                onClick={() => setDateRange(days)}
              >
                {days === 1 ? 'Today' : `${days} days`}
              </button>
            ))}
          </div>
          <button
            className={`goals-filter-btn ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
            title="Filters"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
          </button>
        </div>

        {showFilters && (
          <div className="goals-filters-panel">
            <div className="goals-filter-group">
              <label>Min BTTS</label>
              <div className="goals-filter-chips">
                {[null, 0.5, 0.6, 0.7].map((val) => (
                  <button
                    key={val ?? 'all'}
                    className={`goals-chip goals-chip-sm ${minBttsFilter === val ? 'goals-chip-active' : ''}`}
                    onClick={() => setMinBttsFilter(val)}
                  >
                    {val === null ? 'All' : `${val * 100}%+`}
                  </button>
                ))}
              </div>
            </div>
            <div className="goals-filter-group">
              <label>Min O2.5</label>
              <div className="goals-filter-chips">
                {[null, 0.5, 0.6, 0.7].map((val) => (
                  <button
                    key={val ?? 'all'}
                    className={`goals-chip goals-chip-sm ${minO25Filter === val ? 'goals-chip-active' : ''}`}
                    onClick={() => setMinO25Filter(val)}
                  >
                    {val === null ? 'All' : `${val * 100}%+`}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="goals-summary-stats">
          <span>{data?.fixtures?.length || 0} fixtures</span>
          {dateRange > 1 && (
            <>
              <span className="goals-stat-sep">·</span>
              <span>{dateRange} days</span>
            </>
          )}
        </div>
      </div>

      {/* Streaks Panel */}
      {data?.streaks && data.streaks.length > 0 && (
        <GoalsStreaksPanel streaks={data.streaks} onSelectFixture={onSelectFixture} />
      )}

      {/* Single Table */}
      <div className="goals-table-wrapper">
        {sortedFixtures.length === 0 ? (
          <div className="goals-empty-state">
            <p>No fixtures with predictions for this date</p>
            <button onClick={() => goToDate(1)}>Try tomorrow</button>
          </div>
        ) : (
          <table className="goals-table">
            <thead>
              <tr>
                {dateRange > 1 && <th className="goals-th-date" title="Match Date">Date</th>}
                <SortHeader label="Time" sortKeyName="kickoff" />
                <th className="goals-th-fixture" title="Home Team vs Away Team">Match</th>
                <th className="goals-th-league" title="Competition">Lge</th>
                <SortHeader label="BTTS" sortKeyName="btts" />
                <SortHeader label="O2.5" sortKeyName="o25" />
                <SortHeader label="B+O" sortKeyName="btts_o25" />
                <SortHeader label="1+B" sortKeyName="home_btts" />
                <SortHeader label="2+B" sortKeyName="away_btts" />
                <SortHeader label="1+O" sortKeyName="home_o25" />
                <SortHeader label="2+O" sortKeyName="away_o25" />
                <SortHeader label="xG" sortKeyName="xg" />
              </tr>
            </thead>
            <tbody>
              {sortedFixtures.map((f) => (
                <tr
                  key={f.fixture_id}
                  className="goals-table-row"
                  onClick={() => onSelectFixture(f.fixture_id)}
                >
                  {dateRange > 1 && <td className="goals-td-date">{formatDateShort(f.kickoff)}</td>}
                  <td className="goals-td-time">{formatKickoff(f.kickoff)}</td>
                  <td className="goals-td-fixture">
                    <div className="goals-fixture-teams">
                      <div className="goals-team">
                        {f.home_team?.logo && <img src={f.home_team.logo} alt="" />}
                        <span>{f.home_team?.name || 'Home'}</span>
                      </div>
                      <span className="goals-vs">v</span>
                      <div className="goals-team">
                        {f.away_team?.logo && <img src={f.away_team.logo} alt="" />}
                        <span>{f.away_team?.name || 'Away'}</span>
                      </div>
                    </div>
                  </td>
                  <td className="goals-td-league">
                    {f.competition?.logo && <img src={f.competition.logo} alt="" className="goals-league-icon" />}
                  </td>
                  <td className={`goals-td-prob ${getProbClass(f.btts_yes_prob)}`}>
                    {formatProb(f.btts_yes_prob)}
                  </td>
                  <td className={`goals-td-prob ${getProbClass(f.over_2_5_prob)}`}>
                    {formatProb(f.over_2_5_prob)}
                  </td>
                  <td className={`goals-td-prob ${getProbClass(f.btts_o2_5_prob)}`}>
                    {formatProb(f.btts_o2_5_prob)}
                  </td>
                  <td className={`goals-td-prob ${getProbClass(f.home_win_btts_prob)}`}>
                    {formatProb(f.home_win_btts_prob)}
                  </td>
                  <td className={`goals-td-prob ${getProbClass(f.away_win_btts_prob)}`}>
                    {formatProb(f.away_win_btts_prob)}
                  </td>
                  <td className={`goals-td-prob ${getProbClass(f.home_win_o2_5_prob)}`}>
                    {formatProb(f.home_win_o2_5_prob)}
                  </td>
                  <td className={`goals-td-prob ${getProbClass(f.away_win_o2_5_prob)}`}>
                    {formatProb(f.away_win_o2_5_prob)}
                  </td>
                  <td className="goals-td-xg">
                    {f.expected_total_goals?.toFixed(1) || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default GoalsOverview;
